<template>
  <div
    class="game-table-flush"
    :class="{
      'top20-compact': compact === true || compact === 2 || compact === '2',
      'top20-compact-1': compact === 1 || compact === '1'
    }"
  >
    <vue-good-table
      styleClass="vgt-table condensed vgt-flush"
      v-bind="tableOptions"
      :columns="columns"
      :rows="rankedRows"
      :pagination-options="{ enabled: false }"
      :sort-options="{ enabled: false }"
      :row-click="true"
      @row-click="goToUser"
      compactMode
    >
      <template #table-row="props">
        <div v-if="props.column.field === 'Rank'" class="d-flex justify-content-center">
          <span class="rank-badge">{{ props.row.Rank }}</span>
        </div>
        <div v-else-if="props.column.field === 'Medal'" class="d-flex justify-content-center">
          <fa-icon
            v-if="props.row.Rank <= 3"
            icon="fa-solid fa-medal"
            class="medal-icon"
            :class="medalClass(props.row.Rank)"
          />
        </div>
        <div v-else-if="props.column.field === 'UserNickName'" class="d-flex align-items-center">
          <img
            :src="userAvatarUrl(props.row.UserImage, store.state.imageCacheKey, true)"
            class="avatar avatar-sm me-2 result-avatar"
            :alt="props.row.UserNickName"
          >
          <button
            type="button"
            class="user-info-btn"
            :title="'Profil hráče ' + props.row.UserNickName"
            @click.stop.prevent="goToUser({ row: props.row })"
          >i</button>
          <span>{{ props.row.UserNickName }}</span>
        </div>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
      <template #emptystate>
        <div class="text-center">
          <span>Žebříček je zatím prázdný</span>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { tableOptions } from '../../../../helpers/tableOptions';
import { userAvatarUrl } from '@/helpers/userAvatars';

const props = defineProps({
  rows: Array,
  showGameCount: {
    type: Boolean,
    default: false
  },
  compact: {
    type: [Boolean, String, Number],
    default: false
  }
});

const store = useStore();
const router = useRouter();

const rankedRows = computed(() => {
  return (props.rows || []).slice(0, 20).map((row, index) => ({
    ...row,
    Rank: index + 1,
    GameCount: row.GameCount ?? row.Count ?? row.GameCnt ?? null
  }));
});

const hasYield = computed(() => rankedRows.value.some((row) => row.Yield != null || row.z != null));

const formatYield = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return new Intl.NumberFormat('cs-CZ', {
    style: 'percent',
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero'
  }).format(number);
};

const medalClass = (rank) => {
  if (rank === 1) return 'medal-gold';
  if (rank === 2) return 'medal-silver';
  if (rank === 3) return 'medal-bronze';
  return '';
};

const goToUser = (params) => {
  const userId = params.row?.UserID;
  if (!userId) return;
  router.push({ name: 'Detail her hráče', params: { uid: userId } });
};

const columns = computed(() => {
  const cols = [
    {
      label: 'Pořadí',
      field: 'Rank',
      thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-center',
      tdClass: 'text-sm font-weight-bold text-center',
      width: '90px',
    },
    {
      label: '',
      field: 'Medal',
      sortable: false,
      thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-center',
      tdClass: 'text-sm text-center',
      width: '52px',
    },
    {
      label: 'Hráč',
      field: 'UserNickName',
      thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
      tdClass: 'text-sm font-weight-bold',
    },
  ];
  if (props.showGameCount) {
    cols.push({
      label: 'Hry',
      field: 'GameCount',
      type: 'number',
      thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-center',
      tdClass: 'text-sm font-weight-bold text-center',
      width: '90px',
    });
  }
  if (hasYield.value) {
    cols.push({
      label: 'Výnos',
      field: 'Yield',
      formatFn: formatYield,
      thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
      tdClass: 'text-sm font-weight-bold text-end',
    });
  }
  return cols;
});
</script>

<style scoped>
.game-table-flush :deep(.vgt-table),
.game-table-flush :deep(.vgt-table.vgt-flush) {
  border: none !important;
}

.game-table-flush :deep(.vgt-table tbody tr:last-child td) {
  border-bottom: none !important;
}

.game-table-flush :deep(.vgt-wrap__footer),
.game-table-flush :deep(.vgt-wrap__actions-footer) {
  display: none !important;
}

.game-table-flush :deep(.vgt-responsive) {
  margin-bottom: 0 !important;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
  font-weight: 800;
  font-size: 0.8rem;
  background: rgba(100, 116, 139, 0.14);
  color: #64748B;
}

.medal-icon {
  font-size: 1.15rem;
}

.medal-gold {
  color: #EAB308;
}

.medal-silver {
  color: #94A3B8;
}

.medal-bronze {
  color: #D97706;
}

.user-info-btn {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-right: 8px;
  padding: 0;
  border-radius: 50%;
  border: 1.5px solid #94A3B8;
  background: transparent;
  color: #94A3B8;
  font-size: 0.68rem;
  font-weight: 800;
  font-style: italic;
  font-family: Georgia, 'Times New Roman', serif;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.user-info-btn:hover {
  border-color: #FF8A4C;
  color: #FF8A4C;
}

.top20-compact :deep(.vgt-table td) {
  padding-top: calc(0.35rem - 1px) !important;
  padding-bottom: calc(0.35rem - 1px) !important;
}

.top20-compact-1 :deep(.vgt-table td) {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

.top20-compact-1 :deep(.vgt-table th) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.top20-compact :deep(.rank-badge) {
  min-width: 26px;
  height: 26px;
}

.top20-compact-1 :deep(.rank-badge) {
  min-width: 22px;
  height: 22px;
  font-size: 0.72rem;
}
</style>
